import { useState, useEffect } from 'react'
import {
    LineChart, Line, AreaChart, Area,
    XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts'
import {
    Thermometer, Droplet, Sun, Wind, CloudRain,
    AlertTriangle, Calendar, TrendingUp
} from 'lucide-react'
import { generateEnvironmentData } from '../../utils/mockDataGenerator'
import './EnvironmentDashboard.css'

function EnvironmentDashboard({ crop }) {
    const [envData, setEnvData] = useState(null)

    useEffect(() => {
        setEnvData(generateEnvironmentData())
    }, [crop])

    if (!envData) return <div className="loading">Cargando datos...</div>

    const { current, forecast, gdd } = envData

    return (
        <div className="environment-dashboard">
            <div className="dashboard-header">
                <div>
                    <h2>Monitoreo Ambiental - {crop}</h2>
                    <p>Condiciones climáticas y predicciones meteorológicas</p>
                </div>
            </div>

            {/* Current Conditions */}
            <div className="current-conditions">
                <div className="condition-card glass-card main">
                    <Thermometer size={32} className="condition-icon" />
                    <div className="condition-value">{current.airTemperature}°C</div>
                    <div className="condition-label">Temperatura</div>
                </div>
                <div className="condition-card glass-card">
                    <Droplet size={24} className="condition-icon" />
                    <div className="condition-value">{current.humidity}%</div>
                    <div className="condition-label">Humedad Rel.</div>
                </div>
                <div className="condition-card glass-card">
                    <Sun size={24} className="condition-icon" />
                    <div className="condition-value">{current.solarRadiation}</div>
                    <div className="condition-label">Radiación (W/m²)</div>
                </div>
                <div className="condition-card glass-card">
                    <CloudRain size={24} className="condition-icon" />
                    <div className="condition-value">{current.precipitation}mm</div>
                    <div className="condition-label">Lluvia (24h)</div>
                </div>
                <div className="condition-card glass-card">
                    <Wind size={24} className="condition-icon" />
                    <div className="condition-value">{current.windSpeed}</div>
                    <div className="condition-label">Viento (km/h)</div>
                </div>
            </div>

            {/* Weather Forecast */}
            <div className="forecast-section glass-card">
                <h3><Calendar size={20} /> Pronóstico 7 Días</h3>
                <div className="forecast-grid">
                    {forecast.map((day, index) => (
                        <div key={index} className={`forecast-day ${index === 0 ? 'today' : ''}`}>
                            <div className="day-name">{index === 0 ? 'Hoy' : day.dayName}</div>
                            <div className="day-icon">{day.condition}</div>
                            <div className="day-temps">
                                <span className="temp-max">{day.tempMax}°</span>
                                <span className="temp-min">{day.tempMin}°</span>
                            </div>
                            {day.rain > 0 && (
                                <div className="day-rain">
                                    <CloudRain size={12} /> {day.rain}mm
                                </div>
                            )}
                        </div>
                    ))}
                </div>
            </div>

            {/* GDD Section */}
            <div className="gdd-section glass-card">
                <h3><TrendingUp size={20} /> Grados Día de Crecimiento (GDD)</h3>
                <div className="gdd-stats">
                    <div className="gdd-stat">
                        <div className="gdd-value">{gdd.accumulated}</div>
                        <div className="gdd-label">GDD Acumulados</div>
                    </div>
                    <div className="gdd-stat">
                        <div className="gdd-value">{gdd.dailyAvg}</div>
                        <div className="gdd-label">Promedio Diario</div>
                    </div>
                    <div className="gdd-stat highlight">
                        <div className="gdd-value">{gdd.daysToHarvest}</div>
                        <div className="gdd-label">Días Est. Cosecha</div>
                    </div>
                </div>
            </div>

            {/* Risk Alerts */}
            <div className="risks-section glass-card">
                <h3><AlertTriangle size={20} /> Alertas de Riesgo</h3>
                <div className="risk-items">
                    <div className="risk-item low">
                        <span className="risk-indicator"></span>
                        <div className="risk-content">
                            <strong>Heladas</strong>
                            <p>Riesgo bajo - Temperaturas mínimas por encima de 10°C</p>
                        </div>
                    </div>
                    <div className="risk-item low">
                        <span className="risk-indicator"></span>
                        <div className="risk-content">
                            <strong>Sequía</strong>
                            <p>Sin riesgo - Precipitaciones normales para la época</p>
                        </div>
                    </div>
                    <div className="risk-item medium">
                        <span className="risk-indicator"></span>
                        <div className="risk-content">
                            <strong>Exceso de Humedad</strong>
                            <p>Riesgo moderado - Monitorear drenaje del suelo</p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default EnvironmentDashboard
